import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import SimpleLessonPlanner from "../components/SimpleLessonPlanner";
import DogWithHomework from "../app/assets/img/tl-dog-homework.png";
import SeoMetadata from "../components/SeoMetadata";
import { selectCurrentUser } from "../features/user/userSlice";
import {
  loadLatestPlan,
  loadPlanTimeline,
} from "../features/lessonPlan/lessonPlanSlice";
import {
  SITE_NAME,
  TEACH_LEAGUE_ORIGIN,
  buildWebApplicationStructuredData,
  buildWebSiteStructuredData,
  getSeoImageUrl,
  getTeachLeagueCanonicalUrl,
} from "../utils/seo";

const derivePlannerGreetingName = (user) => {
  const preferredName = user?.preferredName?.trim();
  if (preferredName) return preferredName;

  const displayName = user?.displayName?.trim();
  if (displayName) {
    const [firstWord] = displayName.split(/\s+/).filter(Boolean);
    if (firstWord) return firstWord;
  }

  const emailLocalPart = user?.email?.split("@")[0]?.trim();
  if (emailLocalPart) return emailLocalPart;

  return "";
};

const HomePage = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const requestedPlanRef = useRef(false);
  const [hasPlannerPrompt, setHasPlannerPrompt] = useState(false);
  const [plannerLoading, setPlannerLoading] = useState(false);
  const plannerGreetingName = derivePlannerGreetingName(currentUser);
  const plannerTitle = plannerGreetingName
    ? `Hello ${plannerGreetingName}, what do you want your students to know?`
    : "What do you want your students to know?";
  const title = `${SITE_NAME} | Powered-Up Gradebook and Curriculum Planner`;
  const description =
    "Teach League helps educators plan lessons, analyze missing perspectives, browse curriculum resources, and connect classroom context from Google Drive and Canvas.";
  const canonicalUrl = getTeachLeagueCanonicalUrl("/");
  const imageUrl = getSeoImageUrl(TEACH_LEAGUE_ORIGIN);

  useEffect(() => {
    if (!currentUser) {
      requestedPlanRef.current = false;
      return;
    }

    if (!requestedPlanRef.current) {
      dispatch(loadLatestPlan());
      dispatch(loadPlanTimeline());
      requestedPlanRef.current = true;
    }
  }, [currentUser, dispatch]);

  return (
    <main className="home-landing home-landing--planner-first">
      <SeoMetadata
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        openGraph={{
          title,
          description,
          url: canonicalUrl,
          image: imageUrl,
          imageAlt: "Teach League wave-themed site poster",
        }}
        twitter={{
          title,
          description,
          image: imageUrl,
          imageAlt: "Teach League wave-themed site poster",
        }}
        structuredData={[
          buildWebSiteStructuredData({
            url: canonicalUrl,
            description,
          }),
          buildWebApplicationStructuredData({
            name: SITE_NAME,
            url: canonicalUrl,
            description,
            image: imageUrl,
          }),
        ]}
      />
      <section
        id="home-planner-stage"
        className="home-planner-stage home-planner-stage--top"
        aria-labelledby="home-planner-title"
      >
        <div className="page-shell home-planner-stage__container home-planner-stage__panel--top">
          <div className="home-planner-stage__content">
            <div
              className={`lesson-planner-card lesson-planner-surface home-planner-stage__planner${
                plannerLoading ? " lesson-planner-card--loading" : ""
              }`}
            >
              <SimpleLessonPlanner
                onPlannerLoadingChange={setPlannerLoading}
                onPromptEntryChange={setHasPlannerPrompt}
                shellTitleId="home-planner-title"
                shellTitle={plannerTitle}
                shellDescription="Describe the knowledge, skill, misconception, or standard at the center of the lesson you want to plan next."
              />
              {!hasPlannerPrompt && !plannerLoading && (
                <img
                  className="tl-home-dog"
                  src={DogWithHomework}
                  alt="A terrier trotting off with a page of math homework"
                />
              )}
            </div>
            {/* {!hasPlannerPrompt && (
              <div className="home-planner-stage__info-link-wrap">
                <a
                  href="#home-how-it-works"
                  className="home-planner-stage__info-link"
                >
                  <i className="fa fa-info-circle" aria-hidden="true" />
                  <span>How it Works</span>
                </a>
              </div>
            )} */}
          </div>
        </div>
      </section>

      {!hasPlannerPrompt && !plannerLoading && (
        <section className="tl-home-tiles" aria-label="What Teach League does">
          <div className="page-shell tl-home-tiles__grid">
            <Link className="tl-tile" to="/wizard">
              <span className="tl-tile__kicker">Plan</span>
              <span className="tl-tile__title">Start from the learning goal</span>
              <span className="tl-tile__text">
                Say what students should know. Teach League builds the lesson around your standards,
                accommodations and curriculum.
              </span>
              <span className="tl-tile__cta">Open the lesson planner →</span>
            </Link>
            <Link className="tl-tile" to="/warmups">
              <span className="tl-tile__kicker">Warm-ups</span>
              <span className="tl-tile__title">Open class with a question</span>
              <span className="tl-tile__text">
                Generate a warm-up, or an emergency lesson you can assign through Google Classroom in
                a minute.
              </span>
              <span className="tl-tile__cta">Make a warm-up →</span>
            </Link>
            <Link className="tl-tile" to="/credible/gradebook-demo">
              <span className="tl-tile__kicker">Gradebook</span>
              <span className="tl-tile__title">Know who needs you this week</span>
              <span className="tl-tile__text">
                A gradebook that leads with a short priority queue instead of a wall of numbers.
              </span>
              <span className="tl-tile__cta">See the demo →</span>
            </Link>
          </div>
        </section>
      )}

      {/* {!hasPlannerPrompt && (
        <section
          id="home-how-it-works"
          className="home-hero home-hero--drawer"
          aria-labelledby="home-how-it-works-title"
        >
          <div className="home-hero__container">
            <div className="home-hero__panel home-hero__panel--drawer">
              <div className="home-hero__layout home-hero__layout--drawer">
                <div className="home-hero__wave-layer" aria-hidden="true">
                  <video
                    className="home-hero__wave-video"
                    autoPlay
                    loop
                    playsInline
                    muted
                    poster={WaveLightPoster}
                    preload="auto"
                    disablePictureInPicture
                  >
                    <source
                      src="/videos/waves-boomerang-fast.mp4"
                      type="video/mp4"
                    />
                  </video>
                  <div className="home-hero__wave-glow home-hero__wave-glow--one" />
                  <div className="home-hero__wave-glow home-hero__wave-glow--two" />
                  <div className="home-hero__wave-grid" />
                  <div
                    className="home-hero__wave-sun"
                    aria-hidden="true"
                  />
                </div>

                <div className="home-hero__copy home-hero__copy--drawer">
                  <div className="home-hero__drawer-shell">
                    <div className="home-hero__drawer-header">
                      <h2
                        id="home-how-it-works-title"
                        className="home-hero__closing blend"
                      >
                        <span className="home-hero__closing-line">
                          Think Deeply
                        </span>
                      </h2>
                    </div>

                    <div
                      id="home-how-it-works-panel"
                      className="home-hero__drawer-content is-open"
                      aria-hidden="false"
                    >
                      <div className="home-hero__content">
                        <h3 className="home-hero__title">
                          <span className="home-hero__title-line">
                            <span className="home-hero__title-static">
                              Understand{" "}
                            </span>
                            <Link
                              to="/mydata"
                              className="home-hero__title-link"
                              data-tooltip="See the classes and gradebook context that can shape your next lesson."
                              title="See the classes and gradebook context that can shape your next lesson."
                            >
                              <span className="home-hero__title-link-text">
                                where your students are
                              </span>
                            </Link>{" "}
                            <span className="home-hero__title-static">
                              so you can help them discover{" "}
                            </span>
                            <a
                              href="#home-planner-stage"
                              className="home-hero__title-link"
                              data-tooltip="Jump back to the planner and turn those insights into your next lesson."
                              title="Jump back to the planner and turn those insights into your next lesson."
                            >
                              <span className="home-hero__title-link-text">
                                what's next
                              </span>
                            </a>
                            <span className="home-hero__title-period-group">
                              <span className="home-hero__title-static">.</span>
                            </span>
                          </span>
                        </h3>
                        <p className="home-hero__lede">
                          Teach League helps educators see where students are and
                          design what's next. You can optionally connect Google
                          Drive alongside Canvas to bring in materials, share
                          them with other educators, and keep lesson planning
                          grounded in the work already moving through your
                          classroom.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )} */}
    </main>
  );
};

export default HomePage;
